import { useContext, useEffect } from "react";
import { UsinasContext } from "../context/UsinasContextProvider";
import * as yup from "yup";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import React from "react";
import { Box, Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { Concessionaire, InternetProvider, ReportOptions, Support, Usina } from "../types";
import TextInput from "../../../main/components/TextInput";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DangerBaseButton from "../../../main/components/DangerBaseButton";
import PhoneNumberInput from "../../../main/components/PhoneNumberInput";
import { MainContext } from "../../../main/context/MainContextProvider";
import CheckBoxTableRow from "../../users/components/CheckBoxTableRow";
import SelectInput from "../../../main/components/SelectInput";
import BaseButton from "../../../main/components/BaseButton";
import { AddClusterFormModal } from "./AddClusterFormModal";
import { GenerationType } from "../../operations/types";

const internetProviderSchema = yup.object().shape({
  contactPhone: yup.string().optional().nullable(),
  contactEmail: yup.string().optional().nullable(),
  ip: yup.string().optional().nullable().nullable(),
});
const concessionaireSchema = yup.object().shape({
  contactPhone: yup.string().optional().nullable(),
  contactEmail: yup.string().optional().nullable(),
});
const schema = yup.object().shape({
  internetProviders: yup
    .array()
    .of(internetProviderSchema)
    .optional()
    .nullable(),
  concessionaire: concessionaireSchema.optional().nullable(),
  localDataServerIP: yup.string().optional().nullable(),
  idLocalizacao: yup.string().optional().nullable(),
  dailyReportEnabled: yup.boolean().optional().nullable(),
  monthlyReportEnabled: yup.boolean().optional().nullable(),
  disponibilidadeOnOffEnabled: yup.boolean().optional().nullable(),
  partialReportEnabled: yup.boolean().optional().nullable(),
  cluster: yup.string().optional().nullable(),
  newCluster: yup.string().optional().nullable(),
  phoneNumber: yup.string().optional().nullable(),
  generationType: yup.string()
    .oneOf(Object.values(GenerationType))
    .nullable()
    .optional(),
  plcGatewayDeviceId: yup.string().optional().nullable(),
});
const sismetroSchema = yup.object().shape({
  sismetroIdSolicitante: yup.string().optional().nullable(),
});

export type SupportData = yup.InferType<typeof schema>;
export type InternetProviderData = yup.InferType<typeof internetProviderSchema>;
export type SismetroData = yup.InferType<typeof sismetroSchema>;
export type CombinedData = SupportData & SismetroData;

const combinedSchema = schema.concat(sismetroSchema);

export const inputStyle = {
  width: "250px",
  maxWidth: "100%",
  heigth: "100vh",
};
export const UsinaSupportEditForm = React.forwardRef(
  (
    props: {
      setNewSupport: React.Dispatch<React.SetStateAction<Support | undefined>>;
      setNewReportOprions: React.Dispatch<React.SetStateAction<ReportOptions | undefined>>;
      setNewClusterName: React.Dispatch<React.SetStateAction<string>>;
      newClusterName: string;
      setCluster: React.Dispatch<React.SetStateAction<string | undefined>>;
      setOpenConfirmSupportEditModal: (open: boolean) => void;
      open: boolean;
      setCanEdit: (canEdit: boolean) => void;
      setIdSolicitante: React.Dispatch<
        React.SetStateAction<string | undefined>
      >;
      setGenerationType: React.Dispatch<
        React.SetStateAction<GenerationType | undefined>
      >;
      setPlcGatewayDeviceId: React.Dispatch<React.SetStateAction<string | undefined>>;
    },
    ref
  ) => {
    const {
      control,
      formState: { errors },
      setValue,
      handleSubmit,
      watch,
    } = useForm({
      resolver: yupResolver(combinedSchema),
    });
    const theme = useTheme();
    const { usina, clusters, setClusters } = useContext(UsinasContext);
    const { userIsAdm, userIsClient } = useContext(MainContext);
    const support = usina?.support;

    const {
      setNewSupport,
      setOpenConfirmSupportEditModal,
      setNewReportOprions: setNewReportOptions,
      open,
      setIdSolicitante,
      newClusterName,
      setNewClusterName,
      setCluster,
      setGenerationType,
      setPlcGatewayDeviceId,
    } = props;

    const {
      fields: internetProvidersFields,
      append: appendInternetProvider,
      remove: removeInternetProvider,
    } = useFieldArray({
      control,
      name: "internetProviders",
    });
    const data = watch();

    const generationTypeIsDifferent = data.generationType !== usina?.generationType;

    const supportIsDifferent = checkIfSupportIsDifferent(
      usina,
      support,
      data,
      (usina && "usinaIds" in usina && usina.usinaIds.idSolicitante) || "",
      usina?.generationType?.value
    );

    const reportOptionsIsDifferent = checkIfReportOptionsIsDifferent(
      usina?.reportOptions,
      data
    );

    const clusterIsDifferent = checkIfClusterIsDifferent(
      usina?.clusterId,
      data
    );

    useEffect(() => {
      props.setCanEdit(
        supportIsDifferent || reportOptionsIsDifferent || clusterIsDifferent
      );
    }, [data]);
    const onMount = () => {
      setValue("dailyReportEnabled", usina?.reportOptions?.dailyReportEnabled || false);
      setValue("monthlyReportEnabled", usina?.reportOptions?.monthlyReportEnabled || false);
      setValue("disponibilidadeOnOffEnabled", usina?.reportOptions?.disponibilidadeOnOffEnabled || false);
      setValue("partialReportEnabled", usina?.reportOptions?.partialReportEnabled || false);
      setValue("cluster", usina?.clusterId || "");
      setValue("plcGatewayDeviceId", usina?.plcGatewayDevice?.deviceId ?? "");
      if (!support) {
        setValue("concessionaire.contactEmail", "");
        setValue("concessionaire.contactPhone", "");
        setValue("internetProviders", [{}]);
        setValue("localDataServerIP", "");
        setValue("idLocalizacao", "");
        return;
      }
      setValue(
        "concessionaire.contactEmail",
        support.concessionaire?.contactEmail || ""
      );
      setValue(
        "concessionaire.contactPhone",
        support.concessionaire?.contactPhone || ""
      );
      setValue(
        "sismetroIdSolicitante",
        (usina && "usinaIds" in usina && usina.usinaIds.idSolicitante) || ""
      );
      if (support.internetProviders && support.internetProviders.length > 0) {
        setValue("internetProviders", support.internetProviders);
      } else {
        setValue("internetProviders", [{}]); // Pode definir os valores padrão do provedor aqui
      }
      setValue("localDataServerIP", support.localDataServerIP || "");
      setValue("idLocalizacao", support.idLocalizacao || "");
      setValue("phoneNumber", support.phoneNumber || "");
      setValue("generationType", usina?.generationType?.value || null);
    };

    useEffect(() => {
      if (open) onMount();
    }, [open]);

    const idSolicitanteIsDifferent =
      data.sismetroIdSolicitante !==
      (usina && "usinaIds" in usina && usina.usinaIds.idSolicitante);

    const onSubmit = (data: CombinedData) => {
      const concessionaire: Concessionaire = {
        contactEmail: data.concessionaire?.contactEmail ?? undefined,
        contactPhone: data.concessionaire?.contactPhone ?? undefined,
      };

      const internetProviders: InternetProvider[] =
        data.internetProviders?.map((provider) => {
          return {
            contactEmail: provider.contactEmail ?? undefined,
            contactPhone: provider.contactPhone ?? undefined,
            ip: provider.ip ?? undefined,
          };
        }) ?? [];

      const newSupport: Support | undefined = supportIsDifferent
        ? {
          concessionaire: concessionaire,
          internetProviders: internetProviders,
          localDataServerIP: data.localDataServerIP,
          idLocalizacao: data.idLocalizacao,
          phoneNumber: data.phoneNumber
        }
        : undefined;

      const newReportOptions: ReportOptions | undefined = reportOptionsIsDifferent
        ? {
          monthlyReportEnabled: data.monthlyReportEnabled ?? undefined,
          dailyReportEnabled: data.dailyReportEnabled ?? undefined,
          disponibilidadeOnOffEnabled: data.disponibilidadeOnOffEnabled ?? undefined,
          partialReportEnabled: data.partialReportEnabled ?? undefined
        } : undefined;

      const resolvedPlcGatewayDeviceId = resolvePatchValue(
        usina?.plcGatewayDevice?.deviceId,
        data.plcGatewayDeviceId,
      );
      setPlcGatewayDeviceId(resolvedPlcGatewayDeviceId);

      if (newReportOptions) setNewReportOptions(newReportOptions)

      if (newSupport) {
        setNewSupport(newSupport);
        setIdSolicitante(
          idSolicitanteIsDifferent
            ? data.sismetroIdSolicitante ?? undefined
            : undefined
        );
        setGenerationType(generationTypeIsDifferent ? data.generationType as GenerationType ?? undefined : undefined);
        setOpenConfirmSupportEditModal(true);
      }

      setCluster(data.cluster ?? undefined);
    };
    React.useImperativeHandle(ref, () => ({
      async submitForm() {
        setIdSolicitante(data.sismetroIdSolicitante ?? undefined);
        await handleSubmit(onSubmit)();
      },
    }));

    const [showNewClusterModal, setShowNewClusterModal] = React.useState(false);

    const clusterOptions = clusters?.map((cluster) => ({
      label: cluster.clusterName ?? "",
      value: cluster.clusterId,
    }));

    useEffect(() => {
      if (newClusterName) {
        setValue("newCluster", newClusterName);
        setClusters([...(clusters ?? []), { clusterName: newClusterName, clusterId: "newCluster" }])
        setValue("cluster", "newCluster");
      }
    }, [newClusterName])

    return (
      <Grid
        component={"form"}
        container
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          overflowY: "auto",
        }}
        display={"flex"}
        spacing={2}
      >
        <Grid item xs={12} textAlign={"start"}>
          <Typography sx={{ fontWeight: "bold" }}>Concessionária</Typography>
        </Grid>
        <Grid display={"flex"} item xs={12} md={3}>
          <TextInput
            control={control}
            errors={errors}
            fieldName="concessionaire.contactEmail"
            label="Email"
            sx={inputStyle}
          />
        </Grid>
        <Grid display={"flex"} item xs={12} md={3}>
          <PhoneNumberInput
            control={control}
            errors={errors}
            fieldName="concessionaire.contactPhone"
            label="Telefone"
            sx={inputStyle}
          />
        </Grid>

        <Grid item display={"flex"} xs={12}>
          <Typography
            sx={{
              fontWeight: "bold",
            }}
          >
            Cluster
          </Typography>
        </Grid>

        <Grid display={"flex"} item xs={12}>
          <SelectInput
            fieldName="cluster"
            label="Cluster"
            control={control}
            errors={errors}
            options={clusterOptions ?? []}
            sx={{ width: "100%", maxWidth: "280px", marginRight: "10px" }}
          />
          <Tooltip title={newClusterName ? "novo cluster já registrado" : ""}>
            <Box>
              <BaseButton
                title="Novo Cluster"
                disabled={!!newClusterName}
                startIcon={<AddIcon />}
                onClick={() => setShowNewClusterModal(true)}
                sx={{
                  width: "auto",
                  height: "38px",
                }}
              />
            </Box>
          </Tooltip>
        </Grid>

        <Grid item xs={12} textAlign={"start"}>
          <Typography sx={{ fontWeight: "bold" }}>Permissões de Relatórios</Typography>
        </Grid>
        <Grid display={"flex"} item xs={12} md={3}>
          <CheckBoxTableRow flexDirection="row" active={data.dailyReportEnabled ?? false} onChange={() => { setValue("dailyReportEnabled", !data.dailyReportEnabled) }} name="Relatório Diário" />
        </Grid>
        <Grid display={"flex"} item xs={12} md={3}>
          <CheckBoxTableRow flexDirection="row" active={data.monthlyReportEnabled ?? false} onChange={() => { setValue("monthlyReportEnabled", !data.monthlyReportEnabled) }} name="Relatório Mensal" />
        </Grid>
        <Grid display={"flex"} item xs={12} md={3}>
          <CheckBoxTableRow flexDirection="row" active={data.disponibilidadeOnOffEnabled ?? false} onChange={() => { setValue("disponibilidadeOnOffEnabled", !data.disponibilidadeOnOffEnabled) }} name="Disponibilidade On-Off" />
        </Grid>
        <Grid display={"flex"} item xs={12} md={3}>
          <CheckBoxTableRow flexDirection="row" active={data.partialReportEnabled ?? false} onChange={() => { setValue("partialReportEnabled", !data.partialReportEnabled) }} name="Relatório Parcial" />
        </Grid>

        {/*         <Grid item display={"flex"} xs={12}>
          <Typography
            sx={{
              fontWeight: "bold",
            }}
          >
            Tipo de Geração
          </Typography>
        </Grid>
        <Grid display={"flex"} item xs={12}>
          <CheckBoxTableRow flexDirection="row" active={data.generationType === GenerationType.Solar || data.generationType === null || data.generationType === undefined} onChange={() => { setValue("generationType", GenerationType.Solar) }} name="Solar" />
          <CheckBoxTableRow flexDirection="row" active={data.generationType === GenerationType.Hidrica} onChange={() => { setValue("generationType", GenerationType.Hidrica) }} name="Hídrica" />
        </Grid> */}

        <Grid item display={"flex"} xs={12}>
          <Typography
            sx={{
              fontWeight: "bold",
            }}
          >
            Provedores de Internet
          </Typography>
        </Grid>
        {internetProvidersFields.map((_, index) => (
          <React.Fragment key={index}>
            <Grid item display={"flex"} xs={12}>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                }}
              >
                Provedor {index + 1}
              </Typography>
            </Grid>
            <Grid display={"flex"} item xs={12} md={3}>
              <TextInput
                control={control}
                errors={errors}
                fieldName={`internetProviders.${index}.contactEmail`}
                label="Email"
                sx={inputStyle}
              />
            </Grid>
            <Grid display={"flex"} item xs={12} md={3}>
              <PhoneNumberInput
                control={control}
                errors={errors}
                fieldName={`internetProviders.${index}.contactPhone`}
                label="Telefone"
                sx={inputStyle}
              />
            </Grid>
            <Grid display={"flex"} item xs={12} md={3}>
              <TextInput
                control={control}
                fieldName={`internetProviders[${index}].ip`}
                label="IP"
                errors={errors}
                sx={{
                  width: "100%",
                  maxWidth: "280px",
                  marginBottom: "20px",
                }}
              />
            </Grid>

            {index > 0 && (
              <Grid display={"flex"} item xs={12}>
                <DangerBaseButton
                  onClick={() => {
                    removeInternetProvider(index);
                  }}
                  startIcon={<DeleteIcon />}
                  title="Remover"
                // title="Remover Provedor"
                />
              </Grid>
            )}

            {(userIsAdm || userIsClient) &&
              index === internetProvidersFields.length - 1 && (
                <Grid
                  display={"flex"}
                  item
                  xs={12}
                  style={{
                    justifyContent: "center",
                  }}
                >
                  <PrimaryBaseButton
                    onClick={() => {
                      appendInternetProvider({});
                    }}
                    startIcon={<AddIcon />}
                    title="Adicionar Provedor"
                    // sx={{ height: "38px" }}
                    sx={{
                      height: "38px",
                      [theme.breakpoints.up("md")]: { marginLeft: "auto" },
                    }}
                  />
                </Grid>
              )}
          </React.Fragment>
        ))}

        <Grid item xs={12} textAlign={"start"}>
          <Typography sx={{ fontWeight: "bold" }}>
            IP do Servidor de Dados Local
          </Typography>
        </Grid>
        <Grid display={"flex"} item xs={12} md={3}>
          <TextInput
            sx={{ width: "100%", maxWidth: "280px", marginBottom: "30px" }}
            control={control}
            fieldName="localDataServerIP"
            label="IP"
            errors={errors}
          // width={150}
          />
        </Grid>
        <Grid item xs={12} textAlign={"start"}>
          <Typography sx={{ fontWeight: "bold" }}>Sismetro</Typography>
        </Grid>
        <Grid display={"flex"} item xs={12} md={3}>
          <TextInput
            sx={{ width: "100%", maxWidth: "280px", marginBottom: "30px" }}
            control={control}
            fieldName="sismetroIdSolicitante"
            label="ID do Solicitante"
            errors={errors}
          // width={150}
          />
        </Grid>

        <Grid item xs={12} textAlign={"start"}>
          <Typography sx={{ fontWeight: "bold" }}>
            ID da Localização
          </Typography>
        </Grid>
        <Grid display={"flex"} item xs={12} md={3}>
          <TextInput
            sx={{ width: "100%", maxWidth: "280px", marginBottom: "30px" }}
            control={control}
            fieldName="idLocalizacao"
            label="ID"
            errors={errors}
          // width={150}
          />
        </Grid>

        <Grid item xs={12} textAlign={"start"}>
          <Typography sx={{ fontWeight: "bold" }}>
            Telefone da Usina
          </Typography>
        </Grid>
        <Grid display={"flex"} item xs={12} md={3}>
          <PhoneNumberInput
            control={control}
            errors={errors}
            fieldName="phoneNumber"
            label="Telefone"
            sx={inputStyle}
          />
        </Grid>

        <Grid item xs={12} textAlign={"start"}>
          <Typography sx={{ fontWeight: "bold", marginTop: "30px" }}>
            Nome do Dispositivo de Comandos
          </Typography>
        </Grid>
        <Grid item xs={12} md={3} display="flex" flexDirection="column" rowGap={0.5}>
          <TextInput
            control={control}
            fieldName="plcGatewayDeviceId"
            label="Nome do Dispositivo"
            errors={errors}
          />

          <Typography variant="caption" color="textSecondary">
            <WarningAmberIcon fontSize="small" color="warning" />{" "}
            O 'Nome do Dispositivo de Comandos' deve ser igual ao nome do
            dispositivo virtual criado no IotHub que será usado para a troca
            de mensagens entre o Portal Rop e o PLC instalado na Usina.
          </Typography>
        </Grid>

        <AddClusterFormModal
          showAddFormModal={showNewClusterModal}
          setShowAddFormModal={setShowNewClusterModal}
          itemName="Cluster"
          setNewCluster={setNewClusterName} />
      </Grid>
    );
  }
);

/**
 * Resolve o valor para uma atualização de forma que se o valor já foi definido
 * anteriormente, ele sempre retorne um valor (seja o novo ou o atual), e se o
 * valor não foi definido anteriormente, ele só retorne um valor se um novo
 * valor for fornecido.
 */
const resolvePatchValue = (currentValue: string | null | undefined, newValue: string | null | undefined) => {
  if (currentValue !== null && currentValue !== undefined) {
    return newValue ?? currentValue;
  }
  return newValue || undefined;
}

const checkIfReportOptionsIsDifferent = (
  reportOptions: ReportOptions | undefined,
  data: CombinedData
) => {
  if (data.dailyReportEnabled !== reportOptions?.dailyReportEnabled) return true

  if (data.monthlyReportEnabled !== reportOptions?.monthlyReportEnabled) return true

  if (data.disponibilidadeOnOffEnabled !== reportOptions?.disponibilidadeOnOffEnabled) return true

  if (data.partialReportEnabled !== reportOptions?.partialReportEnabled) return true

  return false;
}

const checkIfClusterIsDifferent = (
  clusterId: string | undefined,
  data: CombinedData
) => {
  if (!clusterId) return true;
  if (clusterId !== data.cluster) return true;
  return false;
};

const checkIfSupportIsDifferent = (
  usina: Usina | null,
  support: Support | undefined,
  data: CombinedData,
  idSolicitante: string,
  generationType: GenerationType | undefined
) => {
  if (!support) return true;
  if (
    support.concessionaire?.contactEmail !== data.concessionaire?.contactEmail
  )
    return true;
  if (
    support.concessionaire?.contactPhone !== data.concessionaire?.contactPhone
  )
    return true;
  if (support.internetProviders && support.internetProviders.length > 0) {
    const isDifferent = support.internetProviders.some((provider, index) => {
      if (data.internetProviders) {
        if (
          provider.contactEmail !== data.internetProviders[index].contactEmail
        ) {
          return true;
        }
        if (
          provider.contactPhone !== data.internetProviders[index].contactPhone
        )
          return true;
        if (provider.ip !== data.internetProviders[index].ip) return true;
      }
      return false;
    });

    if (isDifferent) {
      return true;
    }
  }

  if (support.internetProviders?.length !== data.internetProviders?.length) {
    return true;
  }

  if (support.localDataServerIP !== data.localDataServerIP) {
    return true;
  }
  if (idSolicitante !== data.sismetroIdSolicitante) {
    return true;
  }
  if (support.idLocalizacao !== data.idLocalizacao) {
    return true;
  }
  if (support.phoneNumber !== data.phoneNumber) {
    return true;
  }
  if (generationType !== data.generationType) {
    return true;
  }
  if ((usina?.plcGatewayDevice?.deviceId ?? "") !== (data.plcGatewayDeviceId ?? "")) {
    return true;
  }
  return false;
};
